import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { LeadComparisonService } from './lead-comparison.service';
import { DuplicateLead, EntryMergeMode, LeadComparisonResult, LeadId, ScalarSelection } from '../models/duplicate-lead-merge.types';

export interface MergeWizardState {
  loading: boolean;
  saving: boolean;
  error: string | null;
  candidates: DuplicateLead[];
  keepLeadId: LeadId | null;
  comparison: LeadComparisonResult | null;
  /** fieldKey -> scalar selection */
  scalarSelection: Record<string, ScalarSelection>;
  /** fieldKey -> (entryId -> selected) */
  entrySelection: Record<string, Record<string, boolean>>;
  /** fieldKey -> merge or overwrite the retained lead's own entries */
  entryMode: Record<string, EntryMergeMode>;
}

const INITIAL_STATE: MergeWizardState = {
  loading: false,
  saving: false,
  error: null,
  candidates: [],
  keepLeadId: null,
  comparison: null,
  scalarSelection: {},
  entrySelection: {},
  entryMode: {},
};

/**
 * Single source of truth for the merge modal. Provided at component level so
 * every modal instance owns isolated state. All mutations funnel through here,
 * which keeps the component thin and the selection logic unit-testable.
 */
@Injectable()
export class DuplicateLeadMergeStore {
  private readonly state$$ = new BehaviorSubject<MergeWizardState>(INITIAL_STATE);

  constructor(private readonly comparison: LeadComparisonService) {}

  // ---- selectors ----------------------------------------------------------

  readonly state$: Observable<MergeWizardState> = this.state$$.asObservable();

  get snapshot(): MergeWizardState {
    return this.state$$.value;
  }

  readonly keepLead$ = this.state$.pipe(map(s => this.keepLeadOf(s)));
  readonly duplicates$ = this.state$.pipe(map(s => s.candidates.filter(l => l._id !== s.keepLeadId)));
  readonly selectedCount$ = this.state$.pipe(map(s => this.countSelected(s)));
  /** Number of individual tickboxes currently ticked (what the UI counts). */
  readonly tickedCount$ = this.state$.pipe(map(s => this.countTicked(s)));

  get keepLead(): DuplicateLead | null {
    return this.keepLeadOf(this.snapshot);
  }

  /** Duplicates = every candidate except the kept lead. */
  get duplicateIds(): LeadId[] {
    return this.duplicates.map(l => l._id);
  }

  /** Full duplicate lead objects (every candidate except the kept lead). */
  get duplicates(): DuplicateLead[] {
    const s = this.snapshot;
    return s.candidates.filter(l => l._id !== s.keepLeadId);
  }

  get selectedCount(): number {
    return this.countSelected(this.snapshot);
  }

  get totalCount(): number {
    return this.snapshot.comparison?.totalItems ?? 0;
  }

  get tickedCount(): number {
    return this.countTicked(this.snapshot);
  }

  /**
   * Total tickboxes on screen: one per scalar field (only one alternative can
   * win per field) plus one per mergeable entry (all can be merged).
   */
  get totalTicks(): number {
    const c = this.snapshot.comparison;
    if (!c) return 0;
    return c.scalars.length + c.mergeables.reduce((n, f) => n + f.candidates.length, 0);
  }

  // ---- mutations ----------------------------------------------------------

  setCandidates(candidates: DuplicateLead[]): void {
    this.patch({ ...INITIAL_STATE, candidates });
  }

  setLoading(loading: boolean): void {
    this.patch({ loading });
  }

  setSaving(saving: boolean): void {
    this.patch({ saving });
  }

  setError(error: string | null): void {
    this.patch({ error });
  }

  /** Choose the lead to keep; builds the comparison + default selection. */
  chooseKeep(keepLeadId: LeadId): void {
    const s = this.snapshot;
    const keep = s.candidates.find(l => l._id === keepLeadId);
    if (!keep) return;
    const duplicates = s.candidates.filter(l => l._id !== keepLeadId);
    const comparison = this.comparison.compare(keep, duplicates);
    const { scalarSelection, entrySelection, entryMode } = this.defaultSelection(comparison);
    this.patch({ keepLeadId, comparison, scalarSelection, entrySelection, entryMode });
  }

  /**
   * Default selection is deliberately conservative:
   * - fields where the kept lead is EMPTY are pre-selected (non-destructive fill)
   * - fields that DIFFER are left off (copying would overwrite — user must opt in)
   * - mergeable entries are pre-selected only when the kept lead has none of its own — same
   *   "fill the gap" rule; otherwise the agent ticks the leads whose notes/tags they want
   */
  private defaultSelection(comparison: LeadComparisonResult): Pick<MergeWizardState, 'scalarSelection' | 'entrySelection' | 'entryMode'> {
    const scalarSelection: Record<string, ScalarSelection> = {};
    for (const field of comparison.scalars) {
      const first = field.alternatives[0];
      scalarSelection[field.key] = {
        selected: field.status === 'empty',
        value: first?.value,
        sourceId: first?.source._id,
      };
    }

    const entrySelection: Record<string, Record<string, boolean>> = {};
    const entryMode: Record<string, EntryMergeMode> = {};
    for (const field of comparison.mergeables) {
      entrySelection[field.key] = {};
      entryMode[field.key] = 'merge';
      for (const entry of field.candidates) entrySelection[field.key][entry.id] = field.existing.length === 0;
    }

    return { scalarSelection, entrySelection, entryMode };
  }

  /** Merge into / overwrite the retained lead's own entries for a note-style field. */
  setEntryMode(fieldKey: string, mode: EntryMergeMode): void {
    this.patch({ entryMode: { ...this.snapshot.entryMode, [fieldKey]: mode } });
  }

  setScalarSelected(key: string, selected: boolean): void {
    const current = this.snapshot.scalarSelection[key];
    if (!current) return;
    this.patch({ scalarSelection: { ...this.snapshot.scalarSelection, [key]: { ...current, selected } } });
  }

  /** Pick which alternative value wins for a field (auto-selects the field). */
  setScalarValue(key: string, value: any, sourceId: LeadId): void {
    const current = this.snapshot.scalarSelection[key] ?? { selected: true, value, sourceId };
    this.patch({
      scalarSelection: { ...this.snapshot.scalarSelection, [key]: { ...current, value, sourceId, selected: true } },
    });
  }

  setEntrySelected(fieldKey: string, entryId: string, selected: boolean): void {
    const field = { ...(this.snapshot.entrySelection[fieldKey] ?? {}), [entryId]: selected };
    this.patch({ entrySelection: { ...this.snapshot.entrySelection, [fieldKey]: field } });
  }

  /** Select-all / clear-all across every scalar field and mergeable entry. */
  setAllSelected(selected: boolean): void {
    const comparison = this.snapshot.comparison;
    if (!comparison) return;

    const scalarSelection: Record<string, ScalarSelection> = {};
    for (const field of comparison.scalars) {
      const existing = this.snapshot.scalarSelection[field.key];
      const first = field.alternatives[0];
      scalarSelection[field.key] = {
        selected,
        value: existing?.value ?? first?.value,
        sourceId: existing?.sourceId ?? first?.source._id,
      };
    }

    const entrySelection: Record<string, Record<string, boolean>> = {};
    for (const field of comparison.mergeables) {
      entrySelection[field.key] = {};
      for (const entry of field.candidates) entrySelection[field.key][entry.id] = selected;
    }

    this.patch({ scalarSelection, entrySelection });
  }

  reset(): void {
    this.state$$.next(INITIAL_STATE);
  }

  // ---- internals ----------------------------------------------------------

  private keepLeadOf(s: MergeWizardState): DuplicateLead | null {
    return s.candidates.find(l => l._id === s.keepLeadId) ?? null;
  }

  private countSelected(s: MergeWizardState): number {
    if (!s.comparison) return 0;
    const scalars = s.comparison.scalars.filter(f => s.scalarSelection[f.key]?.selected).length;
    const mergeables = s.comparison.mergeables.filter(f => {
      const map = s.entrySelection[f.key] ?? {};
      return f.candidates.some(c => map[c.id]);
    }).length;
    return scalars + mergeables;
  }

  /** Tick-level count: selected scalars + each individually selected entry. */
  private countTicked(s: MergeWizardState): number {
    if (!s.comparison) return 0;
    const scalars = s.comparison.scalars.filter(f => s.scalarSelection[f.key]?.selected).length;
    const entries = s.comparison.mergeables.reduce((n, f) => n + f.candidates.filter(c => s.entrySelection[f.key]?.[c.id]).length, 0);
    return scalars + entries;
  }

  private patch(partial: Partial<MergeWizardState>): void {
    this.state$$.next({ ...this.state$$.value, ...partial });
  }
}
